import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
  generateQuoteItem,
  getItemInQuote as getItemInQuoteImpl,
  getQuoteLeaseTotal as getQuoteLeaseTotalImpl,
  getQuoteTotal as getQuoteTotalImpl,
  identifyInventoryType,
  identifyQuoteType,
  inventoryTypes,
  quoteTypes,
} from './utils';
import { toCurrencyString, useLocalStorageReducer, useLocalStorageState } from '../../utils';

import { QuoteVisibilityProvider } from '../QuoteVisbility';
import uuid from 'short-uuid';

const storageKey = process.env.GATSBY_LOCAL_STORAGE_KEY || 'cisco-pwp-quote-2020-LOCAL';
const QuoteContext = createContext(null);

export function QuoteProvider({ children }) {
  let [quote, dispatchQuote] = useLocalStorageReducer(storageKey, reducer, []);
  let [quoteId] = useLocalStorageState(`${storageKey}-id`, uuid.generate());
  let getQuoteTotal = useCallback(() => getQuoteTotalImpl(quote), [quote]);
  let getQuoteLeaseTotal = useCallback(() => getQuoteLeaseTotalImpl(quote), [quote]);
  let getTotalCurrencyString = useCallback(() => toCurrencyString(getQuoteTotalImpl(quote)), [quote]);
  let getLeaseTotalCurrencyString = useCallback(() => toCurrencyString(getQuoteLeaseTotalImpl(quote)), [quote]);
  let totalCurrencyStringFormatted = useMemo(() => getTotalCurrencyString(), [getTotalCurrencyString]);
  let leaseTotalCurrencyStringFormatted = useMemo(() => getLeaseTotalCurrencyString(), [getLeaseTotalCurrencyString]);
  let getItemInQuote = useCallback(item => getItemInQuoteImpl(quote, item), [quote]);

  let contextValue = useMemo(
    () => ({
      quote,
      quoteId,
      dispatchQuote,
      getQuoteTotal,
      getQuoteLeaseTotal,
      getTotalCurrencyString,
      getItemInQuote,
      totalCurrencyStringFormatted,
      leaseTotalCurrencyStringFormatted,
    }),
    [
      dispatchQuote,
      getItemInQuote,
      getQuoteLeaseTotal,
      getQuoteTotal,
      getTotalCurrencyString,
      quote,
      quoteId,
      totalCurrencyStringFormatted,
      leaseTotalCurrencyStringFormatted,
    ]
  );

  return (
    <QuoteContext.Provider value={contextValue}>
      <QuoteVisibilityProvider>{children}</QuoteVisibilityProvider>
    </QuoteContext.Provider>
  );
}

export function useQuote() {
  return useContext(QuoteContext);
}

export const quoteActionTypes = {
  add: 'add',
  duplicate: 'duplicate',
  increment: 'increment',
  decrement: 'decrement',
  remove: 'remove',
  setQuantity: 'setQuantity',
  reset: 'reset',
};

function reducer(quote, action) {
  let { add, duplicate, increment, decrement, remove, setQuantity, reset } = quoteActionTypes;
  let { item, type } = action;

  if (!type) throw new Error(`.type of action is needed!`);
  if (type !== reset && !item) throw new Error(`.item is needed in this action!`);
  if (type === setQuantity && !action.quantity)
    throw new Error(`a provided .quantity property is needed in this action to set quantity`);

  switch (type) {
    case add:
      return addToQuote(item, quote);
    case duplicate:
      throw new Error('duplicateInQuote(item, quote) not implemented');
    case remove:
      return removeFromQuote(item, quote);
    case increment:
      return incrementInQuote(item, quote);
    case decrement:
      return decrementInQuote(item, quote);
    case setQuantity:
      return setQuantityInQuote(item, quote, action.quantity);
    case reset:
      return resetQuote();
    default:
      throw new Error(`no such action ${type}`);
  }
}

function addToQuote(item, quote) {
  switch (identifyInventoryType(item)) {
    case inventoryTypes.bundle: {
      const isIndependentProduct = !item.includedProducts;
      if (isIndependentProduct) {
        const { product, selectedMount } = item;
        return addToQuote({ ...product, selectedMount }, quote);
      }
      break;
    }
    case inventoryTypes.product: {
      const existing = getItemInQuoteImpl(quote, item);
      if (existing) {
        if (existing.quantity < 0) existing.quantity = 0;
        existing.quantity += 1;
        return [...quote];
      }
      break;
    }
    default:
      throw new Error(`Cannot add an elem of type ${identifyQuoteType(item)}`);
  }

  const quoteItem = generateQuoteItem(item);
  return [...quote, quoteItem];
}

function removeFromQuote(quoteItem, quote) {
  switch (identifyQuoteType(quoteItem)) {
    case quoteTypes.bundle: {
      return quote.filter(bundle => bundle !== quoteItem);
    }
    case quoteTypes.bundledProduct:
    case quoteTypes.product: {
      return quote.filter(({ product }) => quoteItem.product !== product);
    }
    default:
      throw new Error(`Cannot remove an elem of type ${identifyQuoteType(quoteItem)}`);
  }
}

function incrementInQuote(quoteItem, quote) {
  switch (identifyQuoteType(quoteItem)) {
    case quoteTypes.bundledProduct:
    case quoteTypes.product: {
      quoteItem.quantity = quoteItem.quantity + 1;
      return [...quote];
    }
    case quoteTypes.bundle:
      throw new Error(`Cannot increment a bundle`);
    default:
      throw new Error(`Cannot increment a elem of type ${identifyQuoteType(quoteItem)}`);
  }
}

function decrementInQuote(quoteItem, quote) {
  switch (identifyQuoteType(quoteItem)) {
    case quoteTypes.bundledProduct: {
      const bundledQuantity = quoteItem.bundledProductQuantifier.quantity;
      quoteItem.quantity = quoteItem.quantity <= bundledQuantity ? bundledQuantity : quoteItem.quantity - 1;
      return [...quote];
    }
    case quoteTypes.product: {
      quoteItem.quantity = quoteItem.quantity <= 0 ? 0 : quoteItem.quantity - 1;
      return [...quote];
    }
    case quoteTypes.bundle:
      throw new Error(`Cannot decrement a bundle`);
    default:
      throw new Error(`Cannot decrement a elem of type ${identifyQuoteType(quoteItem)}`);
  }
}

function setQuantityInQuote(quoteItem, quote, quantity) {
  switch (identifyQuoteType(quoteItem)) {
    case quoteTypes.bundledProduct:
    case quoteTypes.product: {
      quantity = isNaN(Number(quantity)) ? 0 : Number(quantity);
      quoteItem.quantity = quantity > 0 ? quantity : 0;
      return [...quote];
    }
    case quoteTypes.bundle:
      throw new Error(
        `Cannot set quantity of a bundle, setQuantityInQuote needs a product type which you find bundled inside the quantityTree object of a bundle`
      );
    default:
      throw new Error(`Cannot set quantity of elem of type ${identifyQuoteType(quoteItem)}`);
  }
}

// function duplicateInQuote(item, quote) {
// TODO when duplicating a bundle we probably need to do a deep clone of the
// quantityTree ot not bring along references and cause bugs by change
// quantity in multiple rooms charing references to same objects
// return null;
// }

export function getSimplifiedQuote(quote) {
  return quote.map(quoteItem => {
    switch (identifyQuoteType(quoteItem)) {
      case quoteTypes.bundle: {
        return {
          title: quoteItem.bundle.title,
          price: quoteItem.bundle.price,
          quantity: quoteItem.quantityTree.main.quantity,
          included: quoteItem.quantityTree.included?.map(included => {
            return {
              title: included.product.heading,
              quantity: included.quantity,
            };
          }),
        };
      }
      case quoteTypes.bundledProduct:
      case quoteTypes.product: {
        return {
          title: quoteItem.product.heading,
          price: quoteItem.product.price,
          quantity: quoteItem.quantity,
        };
      }
      default:
        return {};
    }
  });
}

function resetQuote() {
  return [];
}

export { inventoryTypes, identifyInventoryType, quoteTypes, identifyQuoteType };
